/**
 * F2F inloggen (07-10-2026, besluit Jordy). Alleen owner en admin.
 *
 * F2F laat de server niet zelf inloggen (Cloudflare Turnstile). Hier start je
 * per merk de inloghulp: een GEWONE browser op de server, die je hieronder
 * ziet en bedient (ook op een telefoon). Tik een veld aan en laat de server
 * e-mail, wachtwoord of 2FA-code typen; het Turnstile-vinkje en de Login-klik
 * doe je zelf. Zie je de creatorslijst, druk dan op Done: de server neemt de
 * sessie over (zonder zelf in te loggen) en bewaart hem.
 *
 * Server: /api/admin/f2f-login/* (src/api/routes/admin_f2f_inloggen.py). De
 * beeldverbinding is een websocket zonder token in de URL; het eerste bericht
 * draagt de token, pas daarna komt het eenmalige beeldwachtwoord.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, ApiError, getToken } from '../lib/api'
import { apiUrl } from '../lib/instance'
import { alsAmsterdam } from '../lib/amsterdam'
import { useApi, usePoll } from '../lib/useApi'
import { ErrorNote, Loading, Panel } from '../components/ui'

interface Hulp {
  active: boolean
  phase: string | null
  message: string | null
  selftest: boolean
  started_at: string | null
  ends_at: string | null
  updated_at: string | null
  filled: { field: string; ok: boolean; info: string | null; at: string | null } | null
  can_view: boolean
}

interface Merk {
  brand: 'nova' | 'tt'
  label: string
  session_expires_at: string | null
  helper: Hulp | null
}

interface Stand {
  brands: Merk[]
  selftest_only: boolean
}

const FASE: Record<string, string> = {
  starten: 'Starting…',
  bezig: 'Ready: log in below',
  overnemen: 'Saving and taking over the session (up to a minute)…',
  gelukt: 'Done: session saved',
  mislukt: 'Not saved',
  gestopt: 'Stopped',
  tijd_om: 'Stopped after 20 minutes',
  fout: 'Could not start',
}

function wsUrl(merk: string): string {
  const pad = apiUrl(`/api/admin/f2f-login/${merk}/screen`)
  if (/^https?:\/\//.test(pad)) return pad.replace(/^http/, 'ws')
  return `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}${pad}`
}

/** De browser van de server, via noVNC (alleen op deze pagina ingeladen). */
function Scherm({ merk, onFout }: { merk: string; onFout: (t: string) => void }) {
  const vak = useRef<HTMLDivElement>(null)
  const [stand, zetStand] = useState('Connecting…')
  useEffect(() => {
    let rfb: { disconnect(): void } | null = null
    let dicht = false
    const ws = new WebSocket(wsUrl(merk), ['binary'])
    ws.binaryType = 'arraybuffer'
    ws.onopen = () => ws.send(JSON.stringify({ token: getToken() }))
    ws.onmessage = async (e) => {
      if (typeof e.data !== 'string') return
      let antwoord: { ok?: boolean; password?: string; status?: number } = {}
      try { antwoord = JSON.parse(e.data) } catch { /* geen JSON */ }
      if (!antwoord.ok) {
        onFout(antwoord.status === 403 ? 'Only owner and admin may open the login screen.'
          : antwoord.status === 409 ? 'The login helper is not ready (yet).'
            : 'The login screen could not be opened.')
        ws.close()
        return
      }
      const { default: RFB } = await import('../vendor/novnc/core/rfb.js')
      if (dicht || !vak.current) return
      const r = new RFB(vak.current, ws, { credentials: { password: antwoord.password || '' }, shared: true })
      r.scaleViewport = true
      r.resizeSession = false
      r.addEventListener('connect', () => zetStand(''))
      r.addEventListener('disconnect', () => zetStand('Screen closed.'))
      r.addEventListener('securityfailure', () => onFout('The login screen refused the connection.'))
      rfb = r
      // Pas nu mag de server de beeldverbinding openen: noVNC luistert al.
      window.setTimeout(() => { if (ws.readyState === WebSocket.OPEN) ws.send('go') }, 150)
    }
    ws.onclose = () => zetStand((s) => (s ? s : 'Screen closed.'))
    return () => {
      dicht = true
      try { rfb?.disconnect() } catch { /* al dicht */ }
      try { ws.close() } catch { /* al dicht */ }
    }
  }, [merk, onFout])
  return (
    <div>
      {stand && <div className="mut2" style={{ fontSize: 13, marginBottom: 6 }}>{stand}</div>}
      <div ref={vak} data-testid="f2f-scherm"
           style={{ width: '100%', height: 'min(calc(78vh / var(--dz, 1)), 900px)', background: '#111', borderRadius: 8,
                    overflow: 'hidden', touchAction: 'none' }} />
    </div>
  )
}

function MerkKaart({ m, open, zetOpen, verversen, selftestOnly }: {
  m: Merk
  open: boolean
  zetOpen: (b: boolean) => void
  verversen: () => void
  selftestOnly: boolean
}) {
  const [bezig, zetBezig] = useState('')
  const [fout, zetFout] = useState('')
  const h = m.helper
  const actief = !!h?.active
  const doe = async (wat: string, pad: string, body?: unknown) => {
    zetBezig(wat)
    zetFout('')
    try {
      await api.post(`/api/admin/f2f-login/${m.brand}/${pad}`, body)
      if (pad === 'start') zetOpen(true)
      window.setTimeout(verversen, 800)
    } catch (e) {
      zetFout(e instanceof ApiError ? e.message : 'Something went wrong')
    } finally {
      zetBezig('')
    }
  }
  const onFout = useCallback((t: string) => zetFout(t), [])
  const vervalt = m.session_expires_at
  const binnenkort = vervalt ? new Date(vervalt).getTime() - Date.now() < 2 * 86400_000 : true
  return (
    <Panel title={`F2F ${m.label}`}
           desc={vervalt ? `Saved session valid until ${alsAmsterdam(vervalt)}` : 'No valid saved session'}
           right={!actief ? (
             <button className="btn primary" type="button" disabled={!!bezig}
                     data-testid={`f2f-start-${m.brand}`} onClick={() => doe('start', 'start')}>
               {bezig === 'start' ? 'Starting…' : `Start login ${m.label}`}
             </button>
           ) : (
             <button className="btn ghost sm" type="button" disabled={!!bezig}
                     onClick={() => doe('stop', 'stop')}>Stop</button>
           )}>
      <div style={{ display: 'grid', gap: 10 }}>
        {binnenkort && !actief && (
          <div className={`openbar ${vervalt ? 'u-warn' : 'u-bad'}`}>
            {vervalt ? 'Expires within 2 days: log in again now.' : 'F2F needs a login for this brand.'}
          </div>
        )}
        {h && (
          <div className="mut2" style={{ fontSize: 13 }} data-testid={`f2f-fase-${m.brand}`}>
            <b>{FASE[h.phase || ''] || h.phase}</b>
            {h.message ? ` · ${h.message}` : ''}
            {h.selftest ? ' · SELF-TEST (local test page, not F2F)' : ''}
            {actief && h.ends_at ? ` · stops automatically at ${alsAmsterdam(h.ends_at)}` : ''}
          </div>
        )}
        {actief && h?.phase === 'bezig' && (
          <>
            <ol className="mut2" style={{ fontSize: 13, margin: 0, paddingLeft: 18 }}>
              <li>Tap the email field in the screen below, then press <b>Fill email</b>.</li>
              <li>Tap the password field, then press <b>Fill password</b>.</li>
              <li>Tick the “Verify you are human” box yourself and press <b>Login</b> in the screen.</li>
              <li>Code asked? Tap the code field and press <b>Fill 2FA code</b>.</li>
              <li>When the creators list is visible, press <b>Done</b>. Saving can take up to a minute.</li>
            </ol>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              <button className="btn" type="button" disabled={!!bezig}
                      onClick={() => doe('email', 'fill', { field: 'email' })}>Fill email</button>
              <button className="btn" type="button" disabled={!!bezig}
                      onClick={() => doe('password', 'fill', { field: 'password' })}>Fill password</button>
              <button className="btn" type="button" disabled={!!bezig}
                      onClick={() => doe('code', 'fill', { field: 'code' })}>Fill 2FA code</button>
              <button className="btn primary" type="button" disabled={!!bezig}
                      data-testid={`f2f-done-${m.brand}`}
                      onClick={() => doe('done', 'done')}>Done</button>
              {!open && (
                <button className="btn ghost" type="button" onClick={() => zetOpen(true)}>Show screen</button>
              )}
            </div>
            {h.filled && (
              <div className="mut2" style={{ fontSize: 12 }}>
                Last fill: {h.filled.field} — {h.filled.ok ? 'typed' : `not typed (${h.filled.info})`}
              </div>
            )}
            {open && h.can_view && <Scherm merk={m.brand} onFout={onFout} />}
          </>
        )}
        {selftestOnly && (
          <div className="mut2" style={{ fontSize: 12 }}>
            This server only runs the self-test (local test page). It never contacts F2F.
          </div>
        )}
        {fout && <div className="openbar u-bad" role="alert">{fout}</div>}
      </div>
    </Panel>
  )
}

export default function F2fInloggen() {
  const q = useApi<Stand>('/api/admin/f2f-login/status')
  const [zoek] = useSearchParams()
  const [open, zetOpen] = useState<Record<string, boolean>>({})
  const snel = !!q.data?.brands.some((b) => b.helper?.active)
  usePoll(snel ? 2000 : 15000, q.refetch)
  if (q.loading && !q.data) return <Loading />
  if (q.error) return <ErrorNote error={q.error} />
  const merken = q.data?.brands ?? []
  const eerst = zoek.get('merk')
  const volgorde = eerst ? [...merken].sort((a, b) => (a.brand === eerst ? -1 : b.brand === eerst ? 1 : 0)) : merken
  return (
    <div style={{ display: 'grid', gap: 14, maxWidth: 760 }}>
      <div className="mut2" style={{ fontSize: 13 }}>
        F2F does not let the server log in by itself. Start the login here: the server opens an ordinary
        browser that you see below. It can type the email, password and 2FA code for you; you tick the
        human check and press Login yourself.
      </div>
      {volgorde.map((m) => (
        <MerkKaart key={m.brand} m={m} open={!!open[m.brand]}
                   zetOpen={(b) => zetOpen((o) => ({ ...o, [m.brand]: b }))}
                   verversen={q.refetch} selftestOnly={!!q.data?.selftest_only} />
      ))}
    </div>
  )
}
